 
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { formatEur } from '../lib/workStatus';
import { Landmark, Building2, HandCoins, AlertTriangle, Settings2 } from 'lucide-react';

const Row = ({ label, value, sign, bold, muted, testId }) => (
  <div className={`flex justify-between gap-4 py-2 border-b border-gray-100 last:border-0 ${bold ? 'font-bold text-base text-[#1F3D2B]' : 'text-sm'}`} data-testid={testId}>
    <span className={muted ? 'text-[#2E2E2E]/50' : bold ? '' : 'text-[#2E2E2E]/70'}>{label}</span>
    <span className={`whitespace-nowrap ${sign === '-' ? 'text-red-700' : sign === '+' ? 'text-amber-700' : ''}`}>{sign === '-' ? '− ' : sign === '+' ? '+ ' : ''}{value}</span>
  </div>
);

export const SalaryTaxBreakdown = ({ summary }) => {
  const t = summary.taxes;
  if (!t) return null;
  const profile = summary.tax_profile || {};
  return (
    <div className="space-y-4" data-testid="salary-tax-breakdown">
      {t.warnings?.length > 0 && (
        <div className="space-y-2" data-testid="salary-tax-warnings">
          {t.warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid={`salary-tax-warning-${i}`}><AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {w}</div>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="border-0 shadow-sm" data-testid="salary-tax-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><Landmark className="w-4 h-4 text-[#1F3D2B]" /> 2. Nodokļi</CardTitle>
            <p className="text-xs text-[#2E2E2E]/60">
              {t.vsaoi_status_label} · Grāmatiņa: {profile.tax_book_submitted ? 'iesniegta' : 'nav iesniegta'} · Apgādājamie: {profile.dependents ?? 0}
              {' · '}<Link to="/portal/admin/employees" className="underline">Darbinieka profils</Link>
              {' · '}<Link to="/portal/admin/tax-settings" className="underline inline-flex items-center gap-1"><Settings2 className="w-3 h-3" /> Likmes {t.year}</Link>
            </p>
          </CardHeader>
          <CardContent>
            <Row label="Bruto alga" value={formatEur(t.gross)} testId="salary-tax-gross" />
            <Row label={`Darbinieka VSAOI (${t.employee_vsaoi_rate}%)`} value={formatEur(t.employee_vsaoi)} sign="-" testId="salary-tax-vsaoi" />
            <Row label="Neapliekamais minimums" value={formatEur(t.nontaxable_minimum)} muted={!t.nontaxable_minimum} testId="salary-tax-nontaxable" />
            <Row label={`Atvieglojums par apgādājamiem (${t.dependents})`} value={formatEur(t.dependents_allowance)} muted={!t.dependents_allowance} testId="salary-tax-dependents" />
            <Row label="Invaliditātes atvieglojums" value={formatEur(t.disability_allowance)} muted={!t.disability_allowance} testId="salary-tax-disability" />
            <Row label="Represētās personas atvieglojums" value={formatEur(t.repressed_allowance)} muted={!t.repressed_allowance} testId="salary-tax-repressed" />
            <Row label="Apliekamais ienākums" value={formatEur(t.taxable_income)} testId="salary-tax-taxable" />
            <Row label={`IIN (${t.iin_rate}%)`} value={formatEur(t.iin)} sign="-" testId="salary-tax-iin" />
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card className="border-0 shadow-sm bg-[#1F3D2B] text-white" data-testid="salary-payout-section">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><HandCoins className="w-4 h-4" /> 3. Izmaksājamā alga</CardTitle></CardHeader>
            <CardContent className="text-sm">
              <div className="flex justify-between py-1.5 border-b border-white/15"><span className="text-white/70">Bruto alga</span><span>{formatEur(t.gross)}</span></div>
              <div className="flex justify-between py-1.5 border-b border-white/15"><span className="text-white/70">Darbinieka VSAOI</span><span>− {formatEur(t.employee_vsaoi)}</span></div>
              <div className="flex justify-between py-1.5 border-b border-white/15"><span className="text-white/70">IIN</span><span>− {formatEur(t.iin)}</span></div>
              <div className="flex justify-between py-1.5 border-b border-white/15"><span className="text-white/70">Neto alga</span><span data-testid="salary-net">{formatEur(t.net)}</span></div>
              <div className="flex justify-between py-1.5 border-b border-white/15"><span className="text-white/70">Avansi</span><span>− {formatEur(t.advances)}</span></div>
              <div className="flex justify-between py-3 text-xl font-bold"><span>IZMAKSĀT DARBINIEKAM</span><span data-testid="salary-payout">{formatEur(t.payout)}</span></div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm" data-testid="salary-employer-section">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4 text-[#1F3D2B]" /> 4. Darba devēja izmaksas</CardTitle></CardHeader>
            <CardContent>
              <Row label="Bruto alga" value={formatEur(t.gross)} testId="salary-employer-gross" />
              <Row label={`Darba devēja VSAOI (${t.employer_vsaoi_rate}%)`} value={formatEur(t.employer_vsaoi)} sign="+" testId="salary-employer-vsaoi" />
              <Row label="Uzņēmējdarbības riska nodeva" value={formatEur(t.risk_fee)} sign="+" testId="salary-employer-risk" />
              <Row label="Uzņēmuma kopējās izmaksas" value={formatEur(t.employer_total_cost)} bold testId="salary-employer-total" />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SalaryTaxBreakdown;
